import {
  Component,
  ElementRef,
  HostListener,
  OnInit,
  ViewChild,
} from '@angular/core';
import { Router, NavigationEnd } from '@angular/router';
import { Store } from '@ngrx/store';
import { combineLatest, Observable } from 'rxjs';
import { filter, map, shareReplay, startWith, switchMap } from 'rxjs/operators';
import { Organisation } from '../../organisation/organisation.interface';
import { User } from '../../user/user.interface';
import {
  currentOrganisation,
  organisationById,
  organisationList,
  selectCurrentOrganizationID,
} from '../../organisation/organisation.selector';
import { setCurrentOrganisationID } from '../../organisation/organisation.actions';
import { myUserId, userById } from '../../user/user.selector';
import { AuthenticationService } from '../../authentication/authentication.service';
import { selectCurrentProjectFromEditor } from '../../element-editor/store/selector/editor.selector';
import { take } from 'rxjs/operators';
import { ProjectDescriptor } from '../../elements/resource/types/shape.type';
import {
  selectCurrentProject,
  selectCurrentProjectLabel,
  selectCurrentStudioProjectID,
  setCurrentStudioProjectID,
  setProjectLabel,
  StudioProject,
} from '../../element-editor/store/studio';
import { loadHome } from '../../home/home.actions';

@Component({
  selector: 'nw-global-header',
  templateUrl: './global-header.component.html',
  styleUrls: ['./global-header.component.scss'],
})
export class GlobalHeaderComponent implements OnInit {
  orgs$ = this.store.select(organisationList);
  currentOrgId: string;
  currentOrgId$ = this.store
    .select(selectCurrentOrganizationID)
    .pipe(startWith(null as string | null), shareReplay(1));
  myUserId$ = this.store.select(myUserId);
  myUser$: Observable<User> = this.myUserId$.pipe(
    filter((id): id is string => !!id),
    switchMap(id => this.store.select(userById(id))),
  );

  // Project state (from editor state) //
  currentOrg$: Observable<Organisation | undefined>;
  projectLabel$ = this.store.select(selectCurrentProjectLabel);
  private currentProject$: Observable<StudioProject | undefined>;

  editingProject = false;
  editedProjectName = '';

  // Settings popup state
  showProjectSettings = false;
  projectFontFamily = 'Arial';
  projectPaletteRows: Array<{ name: string; color: string }> = [];

  showOrgDropdown = false;
  showUserDropdown = false;

  /** Whether we're on the /quest/:customToken route (minimal header mode) */
  isQuestRoute = false;

  @ViewChild('orgTrigger') orgTrigger?: ElementRef<HTMLElement>;
  @ViewChild('orgDropdown') orgDropdown?: ElementRef<HTMLElement>;
  @ViewChild('projectSettingsTrigger')
  projectSettingsTrigger?: ElementRef<HTMLElement>;
  @ViewChild('projectSettingsPopup')
  projectSettingsPopup?: ElementRef<HTMLElement>;
  @ViewChild('userDropdown') userDropdown?: ElementRef<HTMLElement>;
  @ViewChild('avatarTrigger') avatarTrigger?: ElementRef<HTMLElement>;
  isRoot = false;

  @ViewChild('projectNameInput')
  projectNameInput?: ElementRef<HTMLInputElement>;
  currentProjectID$ = this.store
    .select(selectCurrentStudioProjectID)
    .pipe(startWith(null as string | null), shareReplay(1));

  currentOrgNoCurrentProject$: Observable<boolean>;

  currentProjectId: string;
  constructor(
    private readonly store: Store,
    private readonly router: Router,
    private readonly authService: AuthenticationService,
  ) {
    this.currentOrgId$.subscribe(id => {
      this.currentOrgId = id;
    });
    this.router.events.subscribe(() => {
      this.isRoot = this.router.url === '/';
    });

    this.currentProject$ = this.store.select(selectCurrentProject);
    this.currentOrg$ = this.store.select(currentOrganisation);

    this.currentOrgNoCurrentProject$ = combineLatest([
      this.currentOrgId$,
      this.currentProjectID$,
    ]).pipe(
      map(([orgId, projectId]) => {
        return !projectId && !!orgId;
      }),
    );
    /* this.myUserId$.subscribe(id => {
      console.log('myUserId', id);
    });

    this.myUser$.subscribe(id => {
      console.log('myUser', id);
    }); */
    // -- //
    this.currentProjectID$.subscribe(id => {
      // console.log('currentProjectID', id);
      this.currentProjectId = id;
    });
    // -- //
  }

  ngOnInit(): void {
    // Check initial route and subscribe to route changes
    this.checkIfQuestRoute(this.router.url);
    this.router.events
      .pipe(filter(event => event instanceof NavigationEnd))
      .subscribe((event: NavigationEnd) => {
        this.checkIfQuestRoute(event.urlAfterRedirects || event.url);
      });

    // this.store.dispatch(loadHome());
    // Only load home data if not on quest route

    // Only set up org-related subscriptions if not on quest route
    if (!this.isQuestRoute) {
      // combineLatest([this.orgs$, this.currentOrgId$])
      //   .pipe(
      //     tap(([orgs, currentId]) => {
      //       if (!currentId && orgs && orgs.length > 0) {
      //         this.store.dispatch(setCurrentOrganisationID({ ID: orgs[0].id }));
      //       }
      //     }),
      //   )
      //   .subscribe();
      // this.currentOrgId$
      //   .pipe(filter((id): id is string => !!id))
      //   .subscribe(id => this.store.dispatch(loadOrganisationById({ id })));
    }
  }

  goToRoot() {
    console.log('goToRoot', this.currentProjectId);
    if (this.currentProjectId) {
      this.router.navigate(['org', this.currentOrgId]);
      this.store.dispatch(setCurrentStudioProjectID({ projectID: null }));
      this.store.dispatch(setCurrentOrganisationID({ ID: this.currentOrgId }));
      return;
    }
    this.store.dispatch(setCurrentStudioProjectID({ projectID: null }));
    this.store.dispatch(setCurrentOrganisationID({ ID: null }));
    this.router.navigate(['']);
  }
  /**
   * Checks if the current URL is the quest route.
   */
  private checkIfQuestRoute(url: string): void {
    this.isQuestRoute = /^\/quest\//.test(url);
  }

  toggleOrgDropdown() {
    this.showOrgDropdown = !this.showOrgDropdown;
  }

  onSelectOrg(id: string) {
    if (!id) return;
    this.store.dispatch(setCurrentOrganisationID({ ID: id }));
    this.showOrgDropdown = false;
  }

  toggleUserDropdown() {
    this.showUserDropdown = !this.showUserDropdown;
  }

  async signOut() {
    this.showUserDropdown = false;
    await this.authService.signOut();
    this.router.navigate(['/signIn']);
  }

  startEditProjectName(current: string) {
    this.editedProjectName = current || 'Untitled Project';
    this.editingProject = true;
    setTimeout(() => {
      console.log('focus', this.projectNameInput?.nativeElement);
      this.projectNameInput?.nativeElement.focus();
      this.projectNameInput?.nativeElement.select();
    }, 5);
  }

  cancelEditProjectName() {
    this.editingProject = false;
  }

  commitProjectName() {
    const label = (this.editedProjectName || '').trim() || 'Untitled Project';
    console.log('commitProjectName', label);
    this.editingProject = false;
    this.store.dispatch(setProjectLabel({ label }));
    // this.currentProjectIRI$.pipe(take(1)).subscribe(IRI => {
    //   if (!IRI) {
    //     this.editingProject = false;
    //     return;
    //   }
    //   this.store.dispatch(setResourceLabel({ IRI, label }));
    //   this.store.dispatch(
    //     setResourcePatch({ IRI, field: 'literals', key: 'label' }),
    //   );
    //   this.editingProject = false;
    // });

    // -- //
  }

  exportProject() {
    // -- //
  }

  // Settings popup
  toggleProjectSettings() {
    if (!this.showProjectSettings) {
      this.openProjectSettings();
    } else {
      this.showProjectSettings = false;
    }
  }

  private openProjectSettings() {
    // this.currentProject$.pipe(take(1)).subscribe(project => {
    //   const descriptor = (project?.literals?.descriptor ||
    //     {}) as ProjectDescriptor;
    //   this.projectFontFamily = descriptor.fontFamily || 'Arial';
    //   const palette = descriptor.colorPalette || descriptor.colorPalette || {};
    //   this.projectPaletteRows = Object.entries(palette).map(
    //     ([name, color]) => ({
    //       name,
    //       color: String(color || ''),
    //     }),
    //   );
    //   if (this.projectPaletteRows.length === 0) {
    //     this.projectPaletteRows = [{ name: '', color: '' }];
    //   }
    //   this.showProjectSettings = true;
    // });
  }

  addPaletteRow() {
    this.projectPaletteRows.push({ name: '', color: '' });
  }

  removePaletteRow(i: number) {
    this.projectPaletteRows.splice(i, 1);
    if (this.projectPaletteRows.length === 0) {
      this.projectPaletteRows.push({ name: '', color: '' });
    }
  }

  onProjectFontSelected(name: any) {
    this.projectFontFamily = String(name || '');
    console.log('onProjectFontSelected', name, this.projectFontFamily);
    // this.store.dispatch(
    //   setDescriptorValue({
    //     key: 'fontFamily',
    //     value: this.projectFontFamily,
    //     IRI: this.currentProjectIRI,
    //     direct: true,
    //   }),
    // );
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent) {
    if (this.showProjectSettings) {
      this.showProjectSettings = false;
    }

    if (this.showOrgDropdown) {
      this.showOrgDropdown = false;
    }

    if (this.showUserDropdown) {
      this.showUserDropdown = false;
    }
    return;

    const target = event.target as Node;

    // Close org dropdown when clicking outside
    if (this.showOrgDropdown) {
      const inTrigger = this.orgTrigger?.nativeElement.contains(target);
      const inDropdown = this.orgDropdown?.nativeElement?.contains(target);
      if (!inTrigger && !inDropdown) {
        this.showOrgDropdown = false;
      }
    }

    // Close project settings when clicking outside
    if (this.showProjectSettings) {
      const inTrigger =
        this.projectSettingsTrigger?.nativeElement.contains(target);
      const inPopup =
        this.projectSettingsPopup?.nativeElement?.contains(target);
      if (!inTrigger && !inPopup) {
        this.showProjectSettings = false;
      }
    }
  }

  getUserName(user: User) {
    if (!user) {
      return '';
    }
    if (user.firstName && user.lastName) {
      return user.firstName + ' ' + user.lastName;
    }
    return user.email;
  }

  getAvatarName(user: User) {
    if (!user) {
      return '';
    }
    if (user.firstName && user.lastName) {
      return user.firstName[0] + user.lastName[0];
    }
    return this.emailToMonogram(user.email);
  }

  emailToMonogram(email, maxInitials = 2) {
    if (typeof email !== 'string') return '';

    const local = email.trim().toLowerCase().split('@')[0] || '';
    if (!local) return '';

    // Prefer dot-splitting if dots exist, otherwise split by broader separators.
    const parts = local.includes('.')
      ? local.split('.')
      : local.split(/[ _+\-]+/);

    // Take the first unicode letter from each token
    const initials = parts
      .map(token => (token.match(/^\P{L}*(\p{L})/u) || [])[1]) // skip leading non-letters, capture first letter
      .filter(Boolean)
      .slice(0, maxInitials)
      .join('');

    // Fallback: if nothing found, grab first letter anywhere in local-part
    const fallback = (local.match(/\p{L}/u) || [''])[0];

    return (initials || fallback).toUpperCase();
  }
}
